import { MessageCircle, PanelRightClose, Trash2, X } from 'lucide-react'
import type { PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { setAssistantForm, setAssistantOpen, type AssistantForm } from '../../../stores/assistant-store'

const ICON_BUTTON =
  'rounded p-1 text-gray-400 hover:bg-black/5 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200'

interface AssistantPanelHeaderProps {
  form: AssistantForm
  /** Whether there is a conversation to clear. */
  canClear: boolean
  onClear: () => void
  /** The floating window's drag start — the header is its handle. Unused in the dock. */
  onDragStart: (event: PointerEvent<HTMLElement>) => void
  dragging: boolean
}

/**
 * The title bar: Clear, bubble ⇄ dock, Close — and, in the floating form, the handle the window is
 * dragged by. A press that starts on one of the buttons is left alone, or the drag would swallow its
 * click.
 */
export function AssistantPanelHeader({ form, canClear, onClear, onDragStart, dragging }: AssistantPanelHeaderProps) {
  const { t } = useTranslation('console')
  const draggable = form === 'bubble'
  const toggleLabel =
    form === 'bubble'
      ? t('assistant.panel.switchToDock', 'Switch to dock view')
      : t('assistant.panel.switchToBubble', 'Switch to bubble view')
  const dragClass = dragging ? 'cursor-grabbing select-none' : 'cursor-grab select-none'

  return (
    <div
      data-testid="assistant-panel-header"
      onPointerDown={
        draggable
          ? event => {
              if ((event.target as HTMLElement).closest('button')) return
              onDragStart(event)
            }
          : undefined
      }
      style={draggable ? { touchAction: 'none' } : undefined}
      className={`flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700 ${
        draggable ? dragClass : ''
      }`}
    >
      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
        {t('assistant.panel.title', 'Assistant')}
      </span>
      <div className="flex items-center gap-1">
        {canClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label={t('assistant.panel.clear', 'Clear conversation')}
            title={t('assistant.panel.clear', 'Clear conversation')}
            data-testid="assistant-clear"
            className={ICON_BUTTON}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setAssistantForm(form === 'bubble' ? 'dock' : 'bubble')}
          aria-label={toggleLabel}
          title={toggleLabel}
          data-testid="assistant-form-toggle"
          className={ICON_BUTTON}
        >
          {form === 'bubble' ? (
            <PanelRightClose className="h-4 w-4" aria-hidden="true" />
          ) : (
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={() => setAssistantOpen(false)}
          aria-label={t('assistant.panel.close', 'Close')}
          title={t('assistant.panel.close', 'Close')}
          data-testid="assistant-close"
          className={ICON_BUTTON}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
